import {
  css,
  DefaultTheme,
  FlattenInterpolation,
  StyledComponent,
  ThemeProps,
} from 'styled-components';
import { Colors, Theme } from '../theme';

export type Css = FlattenInterpolation<ThemeProps<DefaultTheme>>;

export type SelectorStyles = {
  baseStyles: Css;
  hoverStyles: Css;
  activeStyles: Css;
  focusStyles: Css;
  selectedStyles: Css;
};

export const getButtonAsSelectorInteractionStates = (
  selectorStyles: SelectorStyles
): Css => css`
  &:hover {
    ${selectorStyles.hoverStyles}
  }

  &:focus-visible {
    ${selectorStyles.focusStyles}
  }

  &:active {
    ${selectorStyles.activeStyles}
  }
`;

/**
 * @param selectorComponent The styled component for the selector label, eg RadioItem, StyledSegment
 * @param styles The styles for different interaction states
 * @returns a css block to be added to the top level of the Selector component theme override
 */
export const applySelectorStyling = (
  selectorComponent: StyledComponent<'label', DefaultTheme>,
  styles: SelectorStyles
): Css => css`
  ${selectorComponent} {
    ${styles.baseStyles}
  }

  input:not(:checked) {
    :hover + ${selectorComponent} {
      ${styles.hoverStyles}
    }

    // ensures the correct box-shadow when hovering and then active on a non-selected item
    :hover + ${selectorComponent}:active {
      ${styles.activeStyles}
    }

    :active + ${selectorComponent} {
      ${styles.activeStyles}
    }

    :focus-visible + ${selectorComponent} {
      ${styles.focusStyles}
    }

    // ensures the correct box-shadow when hovering on a non-selected focussed item
    :focus-visible:hover + ${selectorComponent} {
      ${styles.hoverStyles}
    }
  }

  input:checked {
    + ${selectorComponent} {
      ${styles.selectedStyles}
    }

    // ensures the correct box-shadow when hovering on a selected item not in focus
    :not(:focus-visible):hover + ${selectorComponent} {
      ${styles.hoverStyles}
    }

    // ensures the correct box-shadow when hovering and then active on a selected item
    :hover + ${selectorComponent}:active {
      ${styles.activeStyles}
    }

    :focus-visible + ${selectorComponent} {
      ${styles.focusStyles}
    }
  }
`;

export type BoxShadow = {
  thicknessInPx: number;
  color: (props: ThemeProps<Theme>) => Colors[keyof Colors];
  isInset?: boolean;
};

export const insetBoxShadow = (
  borderWidth: string,
  borderColor: (props: ThemeProps<Theme>) => Colors[keyof Colors]
) => ({ theme }: ThemeProps<Theme>): string =>
  `inset 0 0 0 ${borderWidth} ${borderColor({ theme })}`;

export const generateBoxShadows = (shadows: BoxShadow[]) => ({
  theme,
}: ThemeProps<Theme>): string =>
  shadows
    .map(
      ({ thicknessInPx, color, isInset }) =>
        `${isInset ? 'inset ' : ''}0 0 0 ${thicknessInPx}px ${color({ theme })}`
    )
    .join(', ');
